import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import CrudCreate from '~/components/CrudCreate';
import IInspectionConfig from '../interfaces/IInspectionConfig';
import Layout from '~/components/Layout';
import { useHookstate } from '@hookstate/core';
import { Checkbox } from '@mui/material';
import { GridActionsCellItem, GridRenderCellParams, GridRowParams } from '@mui/x-data-grid';
import DeleteIcon from '@mui/icons-material/Delete';
import { authFetch } from '~/services/fetch';
import { useLang } from '~/hooks/useLang';
import { capitalizeFirstLetter } from '~/utils/strings';

interface IInspectionItemLine {
  readonly id: number;
  readonly name: string;
  readonly required: boolean;
}

export default function Create() {
  const { register, handleSubmit, control, setValue: setFormValue, watch, getValues, setError } = useForm<IInspectionConfig>();
  const { translate } = useLang();
  const lines = useHookstate<readonly IInspectionItemLine[]>([]);
  const rows = useHookstate<readonly IInspectionItemLine[]>([]);
  const inspectionItemId = useHookstate<readonly number[]>([]);
  
  const nameValue = watch('name');
  React.useEffect(() => {
    if (nameValue) {
      const capitalized = capitalizeFirstLetter(nameValue);
      if (capitalized !== nameValue) {
        setFormValue('name', capitalized, { shouldValidate: false });
      }
    }
  }, [nameValue, setFormValue]);

  useEffect(() => {
    // Busca todos os itens de vistoria
    authFetch({
      url: '/inspection-item',
      method: 'GET',
    }).then((response) => {
      const items = response.data;
      lines.set(items.map((item) => ({
        id: item.id,
        name: item.name,
        required: false,
      })));
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function handleChange(checked: boolean, row: IInspectionItemLine, field: string) {
    const data = rows.get()[rows.get().findIndex((item) => item.id === row.id)];
    rows.set(rows.get().map((rowItem) => rowItem.id === data.id ? { ...data, [field]: Boolean(checked) } : rowItem));
    
    const valueFormatted = rows.get().map((item) => ({
      id: item.id,
      required: item.required ? 1 : 0,
    }));

    setFormValue('inspection_items', valueFormatted);
  }

  function handleDelete(id: number) {
    const rowFiltered = rows.get().filter((row) => row.id !== id);
    rows.set(rowFiltered);
    
    const valueFormatted = rowFiltered.map((item) => ({
      id: item.id,
      required: item.required ? 1 : 0,
    }));

    setFormValue('inspection_items', valueFormatted);
  }

  const columns = [
    { field: 'id', hide: true },
    { field: 'name', headerName: translate('Inspection Item'), flex: 2 },
    {
      field: 'required',
      headerName: translate('Required'),
      flex: 1,
      renderCell: ({ value, row, field }: GridRenderCellParams<IInspectionItemLine>) => (
        <Checkbox
          checked={value as boolean}
          onChange={(event, checked) => handleChange(checked, row, field)}
        />
      ),
    },
    {
      field: 'actions',
      type: 'actions',
      flex: 1,
      getActions: ({ row }: GridRowParams<IInspectionItemLine>) => [
        <GridActionsCellItem
          key="delete"
          icon={<DeleteIcon />}
          onClick={() => handleDelete(row.id)}
          label={translate('Delete')}
          title={translate('Delete')}
        />,
      ],
    },
  ];

  const fields = [
    {
      name: 'device_type_id',
      type: 'selectAutoComplete',
      model: 'device-type',
      placeholder: translate('Device Type'),
      required: true,
    },
    {
      name: 'agency_id',
      type: 'selectAutoComplete',
      model: 'agency',
      placeholder: translate('Agency:name'),
      required: true,
    },
    {
      name: 'inspection_items',
      type: 'table',
      required: true,
      lines: lines.get(),
      columns: columns,
      filterModel: { type: 'inspection_item_id', variable: inspectionItemId },
      rows: rows,
      validate: () => {
        if (rows.get().length) {
          return true;
        } else {
          return translate('Add at least one inspection item.');
        }
      },
    },
  ];

  const wrappedHandleSubmit: typeof handleSubmit = (onValid: any) => {
    return handleSubmit((data: IInspectionConfig) => {
      let hasErrors = false;

      if (!data.device_type_id) {
        setError('device_type_id', { type: 'required', message: translate('Fill in this field.') });
        hasErrors = true;
      }
      if (!data.agency_id) {
        setError('agency_id', { type: 'required', message: translate('Fill in this field.') });
        hasErrors = true; 
      }
      if (hasErrors) return;
      return onValid(data);
    });
  };

  return (
    <Layout marginLayout={true}>
      <CrudCreate
        model="inspection-config"
        fields={fields}
        register={register}
        handleSubmit={wrappedHandleSubmit}
        control={control}
        messageSuccess={translate('Inspection configuration created successfully!')}
        title={translate('Add Inspection Configuration')}
        subheader={translate('Add a new inspection configuration')}
        setFormValue={setFormValue}
        watch={watch}
        getValues={getValues}
      />
    </Layout>
  );
}